"use client";

import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { track } from "@vercel/analytics";
import { Info, XCircle } from "lucide-react";
import { reportError } from "@/lib/report-error";
import { mutate } from "swr";

interface RegistrationFormProps {
  onSuccess: () => void;
}

type Step = "input" | "confirm";

export function RegistrationForm({ onSuccess }: RegistrationFormProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [step, setStep] = useState<Step>("input");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [referrerSessionId, setReferrerSessionId] = useState<string | null>(null);
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [companyTouched, setCompanyTouched] = useState(false);

  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

  const nameError = useMemo(() => {
    if (!nameTouched) return null;
    if (name.trim().length === 0) return "Name is required";
    return null;
  }, [name, nameTouched]);

  const emailError = useMemo(() => {
    if (!emailTouched) return null;
    if (email.trim().length === 0) return "Email is required";
    if (!isValidEmail(email)) return "Please enter a valid email address";
    return null;
  }, [email, emailTouched]);

  const companyError = useMemo(() => {
    if (!companyTouched) return null;
    if (company.trim().length === 0) return "Company is required";
    return null;
  }, [company, companyTouched]);

  useEffect(() => {
    const referrer = searchParams.get("referrer");
    if (referrer) {
      setReferrerSessionId(referrer);
    }
  }, [searchParams]);

  const isValid = useMemo(() => {
    return (
      name.trim().length > 0 &&
      company.trim().length > 0 &&
      email.trim().length > 0 &&
      isValidEmail(email)
    );
  }, [name, company, email]);

  const handleContinueToConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setError("");
    setStep("confirm");
  };

  const handleConfirmSubmit = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("/api/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          name: name.trim(), 
          email: email.trim(),
          company: company.trim(),
          referrerSessionId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to register");
      }

      track(data.isNewUser ? "sign_up" : "sign_in");

      mutate("/api/session");
      mutate("/api/hunt");

      if (referrerSessionId) {
        const newParams = new URLSearchParams(searchParams.toString());
        newParams.delete("referrer");
        const newUrl = newParams.toString() ? `?${newParams.toString()}` : window.location.pathname;
        router.replace(newUrl, { scroll: false });
      }

      onSuccess();
      // Don't reset isLoading on success - let the parent component unmount this form
    } catch (err) {
      reportError(err, { component: "RegistrationForm", action: "handleConfirmSubmit" });
      const errorMessage = err instanceof Error ? err.message : "Something went wrong";
      setError(errorMessage);
      // Reset to input step so user must click Continue again to re-confirm
      setStep("input");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (step === "confirm") {
      const raf = requestAnimationFrame(() => {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [step]);

  if (step === "confirm") {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
          <div className="flex flex-1 flex-col gap-2">
            <Label htmlFor="name-confirm" className="text-base font-medium">
              Name <span className="text-destructive" aria-hidden="true">*</span>
              <span className="sr-only">(required)</span>
            </Label>
            <Input
              id="name-confirm"
              name="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setNameTouched(true)}
              disabled={isLoading}
              required
              className={`h-12 rounded-xl text-base ${nameError ? "border-destructive focus-visible:ring-destructive" : ""}`}
              autoComplete="name"
              aria-required="true"
              aria-invalid={nameError ? "true" : undefined}
              aria-describedby={nameError ? "name-confirm-error" : undefined}
            />
            {nameError && (
              <p id="name-confirm-error" role="alert" className="text-sm text-destructive">
                {nameError}
              </p>
            )}
          </div>

          <div className="flex flex-1 flex-col gap-2">
            <Label htmlFor="email-confirm" className="text-base font-medium">
              Email <span className="text-destructive" aria-hidden="true">*</span>
              <span className="sr-only">(required)</span>
            </Label>
            <Input
              id="email-confirm"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              disabled={isLoading}
              required
              className={`h-12 rounded-xl text-base ${emailError ? "border-destructive focus-visible:ring-destructive" : ""}`}
              autoComplete="email"
              aria-required="true"
              aria-invalid={emailError ? "true" : undefined}
              aria-describedby={emailError ? "email-confirm-error" : undefined}
            />
            {emailError && (
              <p id="email-confirm-error" role="alert" className="text-sm text-destructive">
                {emailError}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="company-confirm" className="text-base font-medium">
            Company <span className="text-destructive" aria-hidden="true">*</span>
            <span className="sr-only">(required)</span>
          </Label>
          <Input
            id="company-confirm"
            name="company"
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            onBlur={() => setCompanyTouched(true)}
            disabled={isLoading}
            required
            className={`h-12 rounded-xl text-base ${companyError ? "border-destructive focus-visible:ring-destructive" : ""}`}
            autoComplete="organization"
            aria-required="true"
            aria-invalid={companyError ? "true" : undefined}
            aria-describedby={companyError ? "company-confirm-error" : undefined}
          />
          {companyError && (
            <p id="company-confirm-error" role="alert" className="text-sm text-destructive">
              {companyError}
            </p>
          )}
        </div>

        {/* Verify notice */}
        <div className="flex items-start gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4 dark:border-sky-800/50 dark:bg-sky-950/40">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden="true" />
          <div className="flex flex-col gap-1">
            <p className="text-base font-medium text-sky-900 dark:text-sky-100">
              Please verify your information
            </p>
            <p className="text-base text-sky-800 dark:text-sky-200/90">
              Prizes are awarded based on your email address. Please make sure it is accurate to ensure prize delivery.
            </p>
          </div>
        </div>

        {/* Confirm button */}
        <Button
          type="button"
          onClick={handleConfirmSubmit}
          disabled={!isValid || isLoading}
          className="h-12 rounded-xl text-base font-semibold sm:w-auto sm:self-start sm:px-8"
        >
          {isLoading ? "Joining..." : "Confirm & Join"}
        </Button>
      </div>
    );
  }

  // Input step (default)
  return (
    <form onSubmit={handleContinueToConfirm} className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
        <div className="flex flex-1 flex-col gap-2">
          <Label htmlFor="name" className="text-base font-medium">
            Name <span className="text-destructive" aria-hidden="true">*</span>
            <span className="sr-only">(required)</span>
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="Enter your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setNameTouched(true)}
            disabled={isLoading}
            required
            className={`h-12 rounded-xl text-base ${nameError ? "border-destructive focus-visible:ring-destructive" : ""}`}
            autoComplete="name"
            autoCapitalize="words"
            autoCorrect="off"
            spellCheck="false"
            inputMode="text"
            enterKeyHint="next"
            aria-required="true"
            aria-invalid={nameError ? "true" : undefined}
            aria-describedby={
              nameError ? "name-error" : error ? "form-error" : undefined
            }
          />
          {nameError && (
            <p id="name-error" role="alert" className="text-sm text-destructive">
              {nameError}
            </p>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-2">
          <Label htmlFor="email" className="text-base font-medium">
            Email <span className="text-destructive" aria-hidden="true">*</span>
            <span className="sr-only">(required)</span>
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            disabled={isLoading}
            required
            className={`h-12 rounded-xl text-base ${emailError ? "border-destructive focus-visible:ring-destructive" : ""}`}
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
            inputMode="email"
            enterKeyHint="next"
            aria-required="true"
            aria-invalid={emailError ? "true" : undefined}
            aria-describedby={
              emailError ? "email-error" : error ? "form-error" : undefined
            }
          />
          {emailError && (
            <p id="email-error" role="alert" className="text-sm text-destructive">
              {emailError}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="company" className="text-base font-medium">
          Company <span className="text-destructive" aria-hidden="true">*</span>
          <span className="sr-only">(required)</span>
        </Label>
        <Input
          id="company"
          name="company"
          type="text"
          placeholder="Your company or organization"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          onBlur={() => setCompanyTouched(true)}
          disabled={isLoading}
          required
          className={`h-12 rounded-xl text-base ${companyError ? "border-destructive focus-visible:ring-destructive" : ""}`}
          autoComplete="organization"
          autoCapitalize="words"
          autoCorrect="off"
          spellCheck="false"
          inputMode="text"
          enterKeyHint="done"
          aria-required="true"
          aria-invalid={companyError ? "true" : undefined}
          aria-describedby={
            companyError ? "company-error" : error ? "form-error" : undefined
          }
        />
        {companyError && (
          <p id="company-error" role="alert" className="text-sm text-destructive">
            {companyError}
          </p>
        )}
      </div>

      {error && (
        <div 
          id="form-error" 
          role="alert" 
          aria-live="polite" 
          className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/30"
        >
          <XCircle className="mt-1 h-5 w-5 shrink-0 text-red-600 dark:text-red-500" />
          <div className="flex flex-col gap-1">
            <p className="text-base font-medium text-red-800 dark:text-red-200">
              {error}
            </p>
            <p className="text-base text-red-700 dark:text-red-300">
              Please check your email address and try again. If you continue to experience issues, please see a team member at the FluidityIQ booth.
            </p>
          </div>
        </div>
      )}

      <Button 
        type="submit" 
        disabled={!isValid || isLoading} 
        className="h-12 rounded-xl text-base font-semibold sm:w-auto sm:self-start sm:px-8"
      >
        Continue
      </Button>
    </form>
  );
}
